import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileCheck2,
  Pill,
  UserRound,
  X,
} from "lucide-react";

import { Button } from "@/design-system/primitives";
import {
  useClinicianTemplates,
  useCreateMissingInfoRequest,
  useRequestClinicianVisit,
  useSubmitPrescriptionDecision,
} from "@/lib/api/clinicianHooks";
import type {
  ClinicianTemplate,
  DoseRequestReasonCode,
  PatientBriefResponse,
  PrescriptionWorkItem,
  PrescriptionAuthorizationPolicy,
} from "@/types/clinician";

import { initialMedicationProduct } from "./medicationSelection";
import { prescriptionMedicationContext } from "./medicationContext";
import { shouldWarnForDefaultNote } from "./defaultNoteWarning";
import { prescriptionDecisionError } from "./prescriptionDecisionError";
import { withApprovalMessageNotice } from "./approvalMessage";
import { MasterProductEvidence } from "./MasterProductEvidence";
import { IntakeHighlights } from "./IntakeHighlights";
import { findIntakeOutliers } from "./intakeOutliers";
import { PrescriptionAuthorization } from "./PrescriptionAuthorization";
import {
  CheckinDoseRequestFields,
  DOSE_REASONS,
  doseAnswerIncomplete,
  type DoseAnswer,
} from "./CheckinDoseRequestFields";

const MISSING_INFO_REASONS = [
  { value: "missing_photo_id", label: "Missing photo ID" },
  { value: "identity_document", label: "Identification or document" },
  { value: "intake_response", label: "Intake answer or clarification" },
  { value: "medical_history", label: "Medical history" },
  { value: "current_medications", label: "Current medications" },
  { value: "allergy_details", label: "Allergy details" },
  { value: "lab_results", label: "Lab result" },
  { value: "visit_required", label: "Visit or appointment" },
  { value: "other", label: "Other" },
] as const;

type MissingInfoReason = (typeof MISSING_INFO_REASONS)[number]["value"];

/** Reasons that mean the intake answers themselves are wrong. For these the
 *  patient can be sent back to the product intake; the backend defaults the
 *  redo on, the checkbox lets the clinician turn it off. */
export const INTAKE_REDO_REASONS: ReadonlySet<string> = new Set([
  "intake_response",
  "medical_history",
  "current_medications",
  "allergy_details",
]);
export type PrescriptionDecision = "approve" | "reject" | "missing_info";

function displayName(item: PrescriptionWorkItem) {
  return item.patient.display_name || item.patient.email || "Unknown patient";
}

function preferredTemplate(
  templates: ClinicianTemplate[],
  decision: "approve" | "reject",
  productId: string,
) {
  const matching = templates.filter(
    (template) =>
      template.decision === decision && template.product_id === productId,
  );
  const global = templates.filter(
    (template) => template.decision === decision && !template.product_id,
  );
  // The list holds shared templates plus only the signed-in clinician's own,
  // so an owner default here is theirs: it wins over the shared default.
  const myDefault = (list: ClinicianTemplate[]) =>
    list.find((template) => template.owner_clinician_id && template.owner_default);
  const shared = (list: ClinicianTemplate[]) =>
    list.filter((template) => !template.owner_clinician_id);
  return (
    myDefault(matching) ??
    myDefault(global) ??
    shared(matching).find((template) => template.is_default) ??
    shared(matching)[0] ??
    shared(global).find((template) => template.is_default) ??
    shared(global)[0] ??
    matching[0] ??
    global[0]
  );
}

export function PrescriptionDecisionModal({
  item,
  data,
  initialDecision,
  onClose,
  onComplete,
}: {
  item: PrescriptionWorkItem;
  data: PatientBriefResponse;
  initialDecision: PrescriptionDecision;
  onClose: () => void;
  onComplete: (message: string, decision: PrescriptionDecision) => void;
}) {
  const templatesQuery = useClinicianTemplates("physician_note");
  const messagesQuery = useClinicianTemplates("message");
  const decisionMutation = useSubmitPrescriptionDecision(item.patient.id);
  const missingInfoMutation = useCreateMissingInfoRequest(item.patient.id);
  const visitMutation = useRequestClinicianVisit(item.patient.id);
  const [doseAnswer, setDoseAnswer] = useState<DoseAnswer>("");
  const [doseChoice, setDoseChoice] = useState("");
  const [doseReason, setDoseReason] = useState<DoseRequestReasonCode | "">("");
  const [doseNote, setDoseNote] = useState("");
  const [missingIntakeRedo, setMissingIntakeRedo] = useState(true);
  const [decision, setDecision] =
    useState<PrescriptionDecision>(initialDecision);
  const options = data.medication_options;
  const savedMedication = data.medication_record;
  const savedAuthorizationPolicy = savedMedication?.authorization_policy as
    | PrescriptionAuthorizationPolicy
    | undefined;
  const [selectedProduct, setSelectedProduct] = useState(
    String(savedMedication?.product_id ?? "") ||
      initialMedicationProduct(options),
  );
  const selectedOption = options.find(
    (option) => option.product_id === selectedProduct,
  );
  const authorizationPolicy = !savedMedication
    ? selectedOption?.authorization_policy
    : undefined;
  const [authorizedRefills, setAuthorizedRefills] = useState(
    selectedOption?.authorization_policy?.refills ?? 0,
  );
  const [authorizationConfirmed, setAuthorizationConfirmed] = useState(false);
  useEffect(() => {
    setAuthorizedRefills(authorizationPolicy?.refills ?? 0);
    setAuthorizationConfirmed(false);
  }, [
    selectedProduct,
    authorizationPolicy?.policy_version,
    authorizationPolicy?.refills,
    authorizationPolicy?.days_per_fill,
    authorizationPolicy?.total_days,
    authorizationPolicy?.review_interval_days,
    authorizationPolicy?.label,
  ]);
  const directionTemplates = selectedOption?.direction_templates ?? [];
  const defaultDirection =
    directionTemplates.find((template) => template.is_default) ??
    directionTemplates[0];
  const [selectedDirectionTemplateId, setSelectedDirectionTemplateId] =
    useState(defaultDirection?.id ?? "");
  const [customDirections, setCustomDirections] = useState(false);
  const [directions, setDirections] = useState(
    defaultDirection?.content ??
      selectedOption?.directions ??
      item.directions ??
      "",
  );
  const [dosage, setDosage] = useState("");
  const [selectedNoteTemplateId, setSelectedNoteTemplateId] = useState("");
  const [note, setNote] = useState(data.clinician_review?.note ?? "");
  const [templateFilledNote, setTemplateFilledNote] = useState("");
  const [defaultNoteWarningAcknowledged, setDefaultNoteWarningAcknowledged] =
    useState(false);
  const [selectedMessageTemplateId, setSelectedMessageTemplateId] =
    useState("");
  const [patientMessage, setPatientMessage] = useState("");
  const patientMessageScope = useRef("");
  const patientMessageEdited = useRef(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [missingReason, setMissingReason] = useState<MissingInfoReason | "">(
    "",
  );
  const [missingDetails, setMissingDetails] = useState("");
  // The programmatic checks the clinician sees on the required intake ride
  // along with a missing-info request so ops knows exactly what to ask for.
  const missingInfoFindings = useMemo(() => {
    const relevant = data.intake_forms.filter((form) =>
      data.relevant_intake_form_ids.includes(form.form_id ?? ""),
    );
    return findIntakeOutliers(relevant.length ? relevant : data.intake_forms)
      .map((outlier) => `${outlier.label} — ${outlier.detail}`)
      .slice(0, 12)
      .map((entry) => entry.slice(0, 300));
  }, [data.intake_forms, data.relevant_intake_form_ids]);
  const [error, setError] = useState<string | null>(null);
  const noteLocked = Boolean(data.clinician_review?.form_answer_group_id);
  // Shown only with the admin flag on, until the prescription is saved: the
  // answer is stored with the medication record, so a retry after a failed
  // write asks again; a saved prescription is a pharmacy retry.
  const doseRequest =
    data.checkin_dose_decision_required && !savedMedication
      ? (data.checkin_dose_request ?? null)
      : null;
  const noteDecision = decision === "missing_info" ? null : decision;
  const messageProductId =
    String(savedMedication?.product_id ?? "") || selectedProduct;
  const approvalNotice =
    noteDecision === "approve"
      ? data.approval_message_notices?.[messageProductId]
      : undefined;
  const availableNoteTemplates = useMemo(
    () =>
      (templatesQuery.data ?? []).filter(
        (template) =>
          template.decision === noteDecision &&
          (!template.product_id || template.product_id === selectedProduct),
      ),
    [noteDecision, selectedProduct, templatesQuery.data],
  );
  const availableMessageTemplates = useMemo(
    () =>
      (messagesQuery.data ?? []).filter(
        (template) =>
          template.decision === noteDecision &&
          (!template.product_id || template.product_id === selectedProduct),
      ),
    [messagesQuery.data, noteDecision, selectedProduct],
  );

  const fillTemplate = (template: ClinicianTemplate) =>
    template.content
      .replaceAll("{{patient}}", displayName(item))
      .replaceAll("{{product}}", selectedOption?.label ?? item.product)
      .replaceAll(
        "{{medication}}",
        selectedOption?.medication ?? item.medication ?? item.product,
      );

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !decisionMutation.isPending) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [decisionMutation.isPending, onClose]);

  useEffect(() => {
    if (!noteDecision || noteLocked) return;
    const selected = preferredTemplate(
      templatesQuery.data ?? [],
      noteDecision,
      selectedProduct,
    );
    const fallback = `${
      noteDecision === "approve" ? "Approved" : "Declined clinically"
    }. Reviewed the patient chart and product-specific intake for ${
      selectedOption?.label ?? item.product
    }.\n\nClinical assessment: `;
    const filled = selected ? fillTemplate(selected) : fallback;
    setSelectedNoteTemplateId(selected?.id ?? "");
    setNote(filled);
    setTemplateFilledNote(filled);
    setDefaultNoteWarningAcknowledged(false);
    // Re-selecting a decision/product intentionally applies its configured
    // default. Free-form edits made afterward remain untouched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteDecision, noteLocked, selectedProduct, templatesQuery.data]);

  useEffect(() => {
    const scope = `${noteDecision}:${messageProductId}`;
    if (patientMessageScope.current !== scope) {
      patientMessageScope.current = scope;
      patientMessageEdited.current = false;
    }
    if (!noteDecision) return;
    if (patientMessageEdited.current) {
      setSelectedMessageTemplateId((current) =>
        availableMessageTemplates.some(
          (template) => template.id === current && template.active,
        )
          ? current
          : "",
      );
      return;
    }
    const selected = preferredTemplate(
      messagesQuery.data ?? [],
      noteDecision,
      selectedProduct,
    );
    setSelectedMessageTemplateId(selected?.id ?? "");
    const message =
      selected
        ? fillTemplate(selected)
        : noteDecision === "approve"
          ? `Your clinician approved your prescription for ${selectedOption?.label ?? item.product}. We are preparing it for the pharmacy now, and we will send another update when it ships.`
          : `Your clinician reviewed your request for ${selectedOption?.label ?? item.product} and was unable to approve it. {{cancellation_summary}} {{refund_summary}} If you have questions, reply here and our team will help.`;
    setPatientMessage(withApprovalMessageNotice(message, approvalNotice));
    // Decision/product changes intentionally choose that scope's configured
    // default. The clinician can then select another variant or edit this
    // one-off copy without changing the saved template. Background template
    // or guidance refreshes must not replace that reviewed copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messagesQuery.data,
    noteDecision,
    selectedProduct,
    messageProductId,
    approvalNotice,
    availableMessageTemplates,
  ]);

  // Keep (or a visit first): the current product with the directions last
  // sent, matched to their template when one has the same text.
  const keepCurrentPrescription = () => {
    const productId = doseRequest?.current_product_id || selectedProduct;
    if (productId !== selectedProduct) changeMedication(productId);
    const current = String(doseRequest?.current_directions ?? "").trim();
    if (!current) return;
    const normalize = (text: string) =>
      text.toLowerCase().split(/\s+/).join(" ");
    const match = options
      .find((option) => option.product_id === productId)
      ?.direction_templates.find(
        (template) => normalize(template.content) === normalize(current),
      );
    setSelectedDirectionTemplateId(match?.id ?? "");
    setDirections(match?.content ?? current);
    setCustomDirections(!match);
  };

  const changeMedication = (productId: string) => {
    const next = options.find((option) => option.product_id === productId);
    const nextTemplate =
      next?.direction_templates.find((template) => template.is_default) ??
      next?.direction_templates[0];
    setSelectedProduct(productId);
    if (productId !== selectedProduct) setDosage("");
    setSelectedDirectionTemplateId(nextTemplate?.id ?? "");
    setDirections(nextTemplate?.content ?? next?.directions ?? "");
    setCustomDirections(false);
  };

  const noteStillMatchesDefault = shouldWarnForDefaultNote({
    note,
    templateNote: templateFilledNote,
    noteLocked,
    acknowledged: false,
  });

  const visitInstead =
    decision === "approve" && Boolean(doseRequest) && doseAnswer === "visit";

  const submit = async () => {
    setError(null);
    if (
      decision === "approve" &&
      !visitInstead &&
      authorizationPolicy &&
      !authorizationConfirmed
    ) {
      setError("Confirm the prescription authorization before signing.");
      return;
    }
    try {
      if (decision === "missing_info") {
        if (!missingReason || !missingDetails.trim()) return;
        const redoOffered = INTAKE_REDO_REASONS.has(missingReason);
        const redoAvailable = Boolean(item.required_intake_form_id);
        const result = await missingInfoMutation.mutateAsync({
          order_id: item.id,
          reason_code: missingReason,
          details: missingDetails.trim(),
          confirmed: true,
          ...(missingInfoFindings.length
            ? { findings: missingInfoFindings }
            : {}),
          ...(redoOffered
            ? { request_intake_redo: missingIntakeRedo && redoAvailable }
            : {}),
        });
        onComplete(
          result?.intake_redo_requested
            ? "Missing information was routed to support and the patient was asked to complete a new intake."
            : "Missing information was routed to support / operations.",
          "missing_info",
        );
        return;
      }
      if (decision === "approve" && doseRequest) {
        if (doseAnswerIncomplete(doseAnswer, doseReason, doseNote, doseChoice)) {
          setError("Answer the patient's dose request before approving.");
          return;
        }
        if (doseAnswer === "visit") {
          const reasonLabel = DOSE_REASONS.find(
            (item) => item.code === doseReason,
          )?.label;
          const visitNote = [reasonLabel, doseNote.trim()]
            .filter(Boolean)
            .join(" — ");
          await visitMutation.mutateAsync({
            order_id: item.id,
            reason_code: "discuss_dosing",
            note: visitNote || undefined,
          });
          onComplete(
            "Visit requested to discuss the dose. The renewal waits for the visit.",
            "approve",
          );
          return;
        }
      }
      if (!note.trim()) return;
      if (decision === "reject" && !rejectionReason.trim()) return;
      if (
        shouldWarnForDefaultNote({
          note,
          templateNote: templateFilledNote,
          noteLocked,
          acknowledged: defaultNoteWarningAcknowledged,
        })
      ) {
        setDefaultNoteWarningAcknowledged(true);
        return;
      }
      const result = await decisionMutation.mutateAsync({
        order_id: item.id,
        decision,
        note: note.trim(),
        rejection_reason:
          decision === "reject" ? rejectionReason.trim() : undefined,
        selected_product_id:
          decision === "approve"
            ? String(savedMedication?.product_id ?? selectedProduct)
            : undefined,
        direction_template_id:
          decision === "approve" &&
          !savedMedication &&
          !customDirections &&
          selectedDirectionTemplateId &&
          !selectedDirectionTemplateId.startsWith("catalog:")
            ? selectedDirectionTemplateId
            : undefined,
        dosage:
          decision === "approve" && !savedMedication
            ? dosage.trim() || undefined
            : undefined,
        directions:
          decision === "approve"
            ? String(savedMedication?.directions ?? directions).trim() ||
              undefined
            : undefined,
        message_template_id: selectedMessageTemplateId || undefined,
        patient_message: patientMessage.trim(),
        ...(decision === "approve" && authorizationPolicy
          ? {
              authorized_refills: authorizedRefills,
              authorization_confirmed: authorizationConfirmed,
            }
          : {}),
        ...(decision === "approve" &&
        doseRequest &&
        doseAnswer !== "" &&
        doseAnswer !== "visit"
          ? {
              dose_request_decision: {
                decision: doseAnswer,
                // The strength chosen in the answer; the backend rejects it
                // unless it is also the medication being prescribed.
                to_product_id:
                  doseAnswer === "increase" || doseAnswer === "change"
                    ? doseChoice
                    : undefined,
                reason_code: doseReason || undefined,
                reason_text: doseNote.trim() || undefined,
              },
            }
          : {}),
        confirmed: true,
      });
      if (decision === "approve") {
        onComplete(
          result.medication?.already_exists
            ? "Approval note confirmed and the existing medication record was linked."
            : "Approval note and medication record were completed together.",
          "approve",
        );
      } else {
        onComplete(
          [
            "Clinical rejection recorded.",
            result.review.subscription_cancelled
              ? "Subscription and order canceled."
              : "Order canceled.",
            result.review.refund_amount_cents != null
              ? `$${(result.review.refund_amount_cents / 100).toFixed(2)} refunded.`
              : "Refund completed.",
            result.review.patient_message_sent
              ? "Patient notified."
              : result.review.patient_message_error,
          ]
            .filter(Boolean)
            .join(" "),
          "reject",
        );
      }
    } catch (error) {
      setError(prescriptionDecisionError(error, decision));
    }
  };

  const busy =
    decisionMutation.isPending ||
    missingInfoMutation.isPending ||
    visitMutation.isPending;
  const submitDisabled = visitInstead
    ? doseAnswerIncomplete(doseAnswer, doseReason, doseNote, doseChoice)
    : decision === "missing_info"
      ? !missingReason || !missingDetails.trim()
      : !note.trim() ||
        !patientMessage.trim() ||
        (decision === "reject" && !rejectionReason.trim()) ||
        (decision === "approve" &&
          Boolean(authorizationPolicy && !authorizationConfirmed)) ||
        (decision === "approve" &&
          Boolean(doseRequest) &&
          doseAnswerIncomplete(doseAnswer, doseReason, doseNote, doseChoice)) ||
        (decision === "approve" &&
          (savedMedication
            ? !savedMedication.product_id || !savedMedication.directions
            : !selectedOption?.medication || !directions.trim()));

  return (
    <div
      className="fixed inset-0 z-[120] bg-ink/55 backdrop-blur-sm p-8 md:p-20 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="prescription-decision-title"
    >
      <div className="min-h-full flex items-start lg:items-center justify-center">
        <section className="w-full max-w-[104rem] bg-page border border-line shadow-xl my-8 grid lg:grid-cols-[minmax(0,1.55fr)_minmax(22rem,0.7fr)] max-h-[calc(100vh-2rem)] overflow-hidden">
          <div className="min-w-0 flex flex-col max-h-[calc(100vh-2rem)]">
            <header className="px-14 py-12 border-b border-line-subtle flex items-start justify-between gap-10 bg-raised">
              <div>
                <p className="type-label-xs text-ink-muted">
                  GUIDED CLINICAL DECISION
                </p>
                <h2
                  id="prescription-decision-title"
                  className="type-heading-lg mt-3"
                >
                  {displayName(item)} · {item.product}
                </h2>
                <PrescriptionSupply
                  summary={
                    selectedOption?.supply_summary ?? item.supply_summary
                  }
                  review
                />
                <p className="type-body-sm text-ink-muted mt-3">
                  The chart note and prescription are submitted as one clinician
                  action.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close decision flow"
                disabled={busy}
                onClick={onClose}
                className="p-5 text-ink-muted hover:text-ink disabled:opacity-40"
              >
                <X size={18} />
              </button>
            </header>

            <div className="p-14 overflow-y-auto flex flex-col gap-14">
              {!noteLocked && (
                <div
                  className="grid grid-cols-3 gap-6"
                  role="tablist"
                  aria-label="Decision type"
                >
                  <DecisionTab
                    active={decision === "approve"}
                    tone="success"
                    label="Approve"
                    onClick={() => setDecision("approve")}
                  />
                  <DecisionTab
                    active={decision === "reject"}
                    tone="danger"
                    label="Reject"
                    onClick={() => setDecision("reject")}
                  />
                  <DecisionTab
                    active={decision === "missing_info"}
                    tone="warning"
                    label="Missing info"
                    onClick={() => setDecision("missing_info")}
                  />
                </div>
              )}

              {decision === "approve" && doseRequest && (
                <CheckinDoseRequestFields
                  request={doseRequest}
                  options={options}
                  templates={selectedOption?.direction_templates ?? []}
                  containerCapMl={selectedOption?.container_cap_ml}
                  answer={doseAnswer}
                  choice={doseChoice}
                  reason={doseReason}
                  note={doseNote}
                  onAnswer={(value) => {
                    setDoseAnswer(value);
                    setDoseChoice("");
                    setDoseReason("");
                  }}
                  onKeepCurrent={keepCurrentPrescription}
                  onChooseProduct={(productId) => {
                    changeMedication(productId);
                    setDoseChoice(productId);
                  }}
                  onChooseTemplate={(template) => {
                    setSelectedDirectionTemplateId(template.id);
                    setDirections(template.content);
                    setCustomDirections(false);
                    setDoseChoice(template.id);
                  }}
                  onReason={setDoseReason}
                  onNote={setDoseNote}
                />
              )}

              {decision === "missing_info" ? (
                <MissingInfoFields
                  reason={missingReason}
                  details={missingDetails}
                  onReason={setMissingReason}
                  onDetails={setMissingDetails}
                  intakeRedo={missingIntakeRedo}
                  onIntakeRedo={setMissingIntakeRedo}
                  intakeRedoAvailable={Boolean(item.required_intake_form_id)}
                />
              ) : (
                <>
                  <div className="flex flex-col gap-7">
                    <div className="flex items-center gap-7">
                      <FileCheck2 size={15} />
                      <div>
                        <p className="type-label-sm font-medium">
                          Physician note
                        </p>
                        <p className="text-11 text-ink-muted">
                          {noteLocked
                            ? "This note is already locked; completing approval will resume with the medication record."
                            : "Choose a default, review it, and add your clinical assessment."}
                        </p>
                      </div>
                    </div>
                    {!noteLocked && availableNoteTemplates.length > 0 && (
                      <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                        Note template
                        <select
                          value={selectedNoteTemplateId}
                          onChange={(event) => {
                            const template = availableNoteTemplates.find(
                              (entry) => entry.id === event.target.value,
                            );
                            if (!template) return;
                            const filled = fillTemplate(template);
                            setSelectedNoteTemplateId(template.id);
                            setNote(filled);
                            setTemplateFilledNote(filled);
                            setDefaultNoteWarningAcknowledged(false);
                          }}
                          className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink"
                        >
                          {availableNoteTemplates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.product_id ? "Product · " : "Global · "}
                              {template.is_default ? "Default · " : ""}
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <textarea
                      value={note}
                      onChange={(event) => {
                        setNote(event.target.value);
                        setDefaultNoteWarningAcknowledged(false);
                      }}
                      readOnly={noteLocked}
                      rows={6}
                      aria-label="Physician note"
                      className="border border-line bg-raised px-10 py-9 type-body-sm outline-none focus:shadow-focus resize-y read-only:bg-sunken read-only:text-ink-secondary"
                    />
                  </div>

                  {noteStillMatchesDefault &&
                    defaultNoteWarningAcknowledged && (
                      <div className="border border-warning/40 bg-warning-wash p-10 flex gap-7">
                        <AlertTriangle
                          size={16}
                          className="text-warning shrink-0 mt-1"
                          aria-hidden
                        />
                        <div>
                          <p className="type-label-sm text-warning">
                            Physician note is still the default template
                          </p>
                          <p className="type-body-sm text-ink-secondary mt-3">
                            Add the patient-specific assessment, or click the
                            submit action again to confirm this default note is
                            intentional. This warning appears only once.
                          </p>
                        </div>
                      </div>
                    )}

                  {decision === "reject" ? (
                    <label className="flex flex-col gap-4">
                      <span className="type-label-xs text-danger">
                        REJECTION REASON · REQUIRED
                      </span>
                      <textarea
                        value={rejectionReason}
                        onChange={(event) =>
                          setRejectionReason(event.target.value)
                        }
                        rows={4}
                        placeholder="Explain the clinical reason for rejection. This is recorded with the locked physician note."
                        className="border border-danger/40 bg-danger-wash px-10 py-9 type-body-sm outline-none focus:shadow-focus resize-y"
                      />
                    </label>
                  ) : savedMedication ? (
                    <div className="border border-warning/30 bg-warning-wash p-11 flex flex-col gap-7">
                      <p className="type-label-sm">
                        Saved prescription · pharmacy submission pending
                      </p>
                      <p className="type-body-sm font-medium">
                        {String(savedMedication.name ?? "")}
                      </p>
                      <p className="type-body-sm whitespace-pre-wrap">
                        {String(savedMedication.directions ?? "")}
                      </p>
                      <p className="text-11 text-ink-muted">
                        Retry sends this saved prescription. The locked note,
                        medication, and directions are retained.
                      </p>
                    </div>
                  ) : selectedOption ? (
                    <MedicationFields
                      options={options}
                      selectedProduct={selectedProduct}
                      selectedOption={selectedOption}
                      selectedDirectionTemplateId={selectedDirectionTemplateId}
                      customDirections={customDirections}
                      directions={directions}
                      dosage={dosage}
                      onProduct={changeMedication}
                      onDirectionTemplate={(value) => {
                        if (value === "__custom") {
                          setCustomDirections(true);
                          return;
                        }
                        const template = directionTemplates.find(
                          (entry) => entry.id === value,
                        );
                        setCustomDirections(false);
                        setSelectedDirectionTemplateId(value);
                        setDirections(template?.content ?? "");
                      }}
                      onDirections={setDirections}
                      onDosage={setDosage}
                    />
                  ) : options.length ? (
                    <div className="border border-warning/50 bg-warning-wash p-11 flex flex-col gap-8">
                      <div className="flex gap-7">
                        <AlertTriangle
                          size={15}
                          className="shrink-0 mt-1 text-warning"
                        />
                        <div>
                          <p className="type-label-sm font-medium">
                            Choose the exact medication and dose
                          </p>
                          <p className="type-body-sm text-ink-secondary mt-2">
                            This purchase does not preselect a fulfillment SKU.
                            Select the prescribed option before approval; the
                            system will not choose one automatically.
                          </p>
                        </div>
                      </div>
                      <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                        Medication / dose
                        <select
                          value=""
                          onChange={(event) => {
                            changeMedication(event.target.value);
                            setDoseAnswer("");
                            setDoseChoice("");
                          }}
                          className="min-h-40 border border-warning/50 bg-raised px-10 type-body-sm text-ink"
                        >
                          <option value="" disabled>
                            Choose exact medication / dose…
                          </option>
                          {options.map((option) => (
                            <option
                              key={option.product_id}
                              value={option.product_id}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-danger/30 bg-danger-wash p-9 flex gap-7 text-danger type-body-sm">
                      <AlertTriangle size={15} className="shrink-0 mt-1" />
                      <p>
                        No medication format is configured for this purchase.
                        Add or repair the product-to-medication mapping before
                        approval.
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col gap-7 border-t border-line-subtle pt-12">
                    <div className="flex items-center gap-7">
                      <CheckCircle2 size={15} />
                      <div>
                        <p className="type-label-sm font-medium">
                          Automated patient message
                        </p>
                        <p className="text-11 text-ink-muted">
                          Choose the saved variant for this decision, then
                          review or personalize the one-off message that will be
                          sent after the workflow succeeds.
                        </p>
                      </div>
                    </div>
                    {availableMessageTemplates.length > 0 && (
                      <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                        MESSAGE TEMPLATE
                        <select
                          value={selectedMessageTemplateId}
                          onChange={(event) => {
                            const template = availableMessageTemplates.find(
                              (entry) => entry.id === event.target.value,
                            );
                            if (!template) return;
                            patientMessageEdited.current = true;
                            setSelectedMessageTemplateId(template.id);
                            setPatientMessage(
                              withApprovalMessageNotice(
                                fillTemplate(template),
                                approvalNotice,
                              ),
                            );
                          }}
                          className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink"
                        >
                          {availableMessageTemplates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.product_id ? "Product · " : "Global · "}
                              {template.is_default ? "Default · " : ""}
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <textarea
                      value={patientMessage}
                      onChange={(event) => {
                        patientMessageEdited.current = true;
                        setPatientMessage(event.target.value);
                      }}
                      rows={5}
                      aria-label="Automated patient message"
                      className="border border-line bg-accent-wash/30 px-10 py-9 type-body-sm outline-none focus:shadow-focus resize-y"
                    />
                    {decision === "reject" &&
                      (patientMessage.includes("{{cancellation_summary}}") ||
                        patientMessage.includes("{{refund_summary}}")) && (
                        <p className="text-11 text-ink-muted">
                          Cancellation and refund placeholders are replaced with
                          the completed result before this is sent.
                        </p>
                      )}
                  </div>
                </>
              )}

              {decision === "approve" && authorizationPolicy && (
                <PrescriptionAuthorization
                  policy={authorizationPolicy}
                  refills={authorizedRefills}
                  confirmed={authorizationConfirmed}
                  onRefills={(value) => {
                    setAuthorizedRefills(value);
                    setAuthorizationConfirmed(false);
                  }}
                  onConfirm={setAuthorizationConfirmed}
                  disabled={busy}
                />
              )}
              {decision === "approve" && savedAuthorizationPolicy && (
                <div className="flex flex-col gap-8">
                  <p className="type-label-sm">
                    Saved authorization — locked on retry
                  </p>
                  <PrescriptionAuthorization
                    policy={savedAuthorizationPolicy}
                    refills={savedAuthorizationPolicy.refills}
                    confirmed
                    disabled
                    onRefills={() => undefined}
                    onConfirm={() => undefined}
                  />
                </div>
              )}

              {error && (
                <div className="border border-danger/30 bg-danger-wash p-9 flex gap-7 text-danger type-body-sm">
                  <AlertTriangle size={15} className="shrink-0 mt-1" />
                  <p>{error}</p>
                </div>
              )}
            </div>

            <footer className="px-14 py-11 border-t border-line-subtle bg-raised flex flex-wrap items-center justify-between gap-8">
              <p className="text-11 text-ink-muted max-w-[38rem]">
                {decision === "approve"
                  ? "Submitting locks the physician note, writes or links the medication record, advances the pharmacy order, and notifies the patient."
                  : decision === "reject"
                    ? "Submitting locks the rejection note, cancels the order/subscription, issues the refund, and notifies the patient."
                    : "Support will receive the selected reason and your required explanation."}
              </p>
              <div className="flex items-center gap-7">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  loading={busy}
                  disabled={submitDisabled}
                  onClick={submit}
                  className={
                    decision === "approve"
                      ? "bg-success text-white border-success hover:bg-success/90"
                      : decision === "reject"
                        ? "bg-danger text-white border-danger hover:bg-danger/90"
                        : "bg-warning text-ink border-warning hover:bg-warning/85"
                  }
                >
                  {decision === "approve"
                    ? savedMedication
                      ? "Retry pharmacy submission"
                      : noteLocked
                        ? "Complete prescription"
                        : "Approve & prescribe"
                    : decision === "reject"
                      ? "Reject prescription"
                      : "Send to support / ops"}
                </Button>
              </div>
            </footer>
          </div>

          <PatientContext item={item} data={data} />
        </section>
      </div>
    </div>
  );
}

function DecisionTab({
  active,
  tone,
  label,
  onClick,
}: {
  active: boolean;
  tone: "success" | "danger" | "warning";
  label: string;
  onClick: () => void;
}) {
  const colors = {
    success: active
      ? "bg-success text-white border-success"
      : "border-success/40 text-success bg-success-wash",
    danger: active
      ? "bg-danger text-white border-danger"
      : "border-danger/40 text-danger bg-danger-wash",
    warning: active
      ? "bg-warning text-ink border-warning"
      : "border-warning/40 text-warning bg-warning-wash",
  };
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`min-h-40 border type-label-sm ${colors[tone]}`}
    >
      {label}
    </button>
  );
}

function MissingInfoFields({
  reason,
  details,
  onReason,
  onDetails,
  intakeRedo,
  onIntakeRedo,
  intakeRedoAvailable,
}: {
  reason: MissingInfoReason | "";
  details: string;
  onReason: (value: MissingInfoReason | "") => void;
  onDetails: (value: string) => void;
  intakeRedo: boolean;
  onIntakeRedo: (value: boolean) => void;
  intakeRedoAvailable: boolean;
}) {
  const redoOffered = reason !== "" && INTAKE_REDO_REASONS.has(reason);
  return (
    <div className="border border-warning/30 bg-warning-wash p-11 flex flex-col gap-9">
      <div>
        <p className="type-label-sm text-warning">
          What information is missing?
        </p>
        <p className="text-11 text-ink-muted mt-3">
          Prescribing pauses until support resolves this request.
        </p>
      </div>
      <select
        value={reason}
        onChange={(event) =>
          onReason(event.target.value as MissingInfoReason | "")
        }
        className="min-h-40 border border-line bg-raised px-10 type-body-sm"
        aria-label="Missing information reason"
      >
        <option value="">Select missing information</option>
        {MISSING_INFO_REASONS.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {entry.label}
          </option>
        ))}
      </select>
      {redoOffered && (
        <label className="flex items-start gap-8 type-label-sm">
          <input
            type="checkbox"
            checked={intakeRedo && intakeRedoAvailable}
            disabled={!intakeRedoAvailable}
            onChange={(event) => onIntakeRedo(event.target.checked)}
            aria-label="Ask the patient to complete a new intake"
            className="mt-2"
          />
          <span>
            Ask the patient to complete a new intake
            <span className="block text-11 text-ink-muted mt-2">
              {intakeRedoAvailable
                ? "They get an email/text with a link; the form reappears in their portal. The hold clears on its own when the new intake comes in."
                : "Not available for this order — it has no product intake form. Support will follow up instead."}
            </span>
          </span>
        </label>
      )}
      {reason === "missing_photo_id" ? (
        <p className="border border-line bg-raised px-10 py-8 text-11 text-ink-muted">
          System Support will request a readable ID front now, after 24 hours,
          and after 48 hours. A new upload that passes the front-of-ID check
          closes the support ticket and returns this prescription to the
          clinical queue. Back-only or uncertain uploads do not clear it.
        </p>
      ) : null}
      <label className="flex flex-col gap-4">
        <span className="type-label-xs text-ink-muted">
          EXPLANATION · REQUIRED
        </span>
        <textarea
          value={details}
          onChange={(event) => onDetails(event.target.value)}
          rows={5}
          placeholder="Explain what needs to be collected or corrected and what support should tell the patient."
          className="border border-line bg-raised px-10 py-9 type-body-sm outline-none focus:shadow-focus resize-y"
        />
      </label>
    </div>
  );
}

function MedicationFields({
  options,
  selectedProduct,
  selectedOption,
  selectedDirectionTemplateId,
  customDirections,
  directions,
  dosage,
  onProduct,
  onDirectionTemplate,
  onDirections,
  onDosage,
}: {
  options: PatientBriefResponse["medication_options"];
  selectedProduct: string;
  selectedOption: PatientBriefResponse["medication_options"][number];
  selectedDirectionTemplateId: string;
  customDirections: boolean;
  directions: string;
  dosage: string;
  onProduct: (value: string) => void;
  onDirectionTemplate: (value: string) => void;
  onDirections: (value: string) => void;
  onDosage: (value: string) => void;
}) {
  return (
    <div className="border border-line-subtle p-11 flex flex-col gap-9">
      <div className="flex items-center gap-7">
        <Pill size={15} />
        <div>
          <p className="type-label-sm font-medium">Medication record</p>
          <p className="text-11 text-ink-muted">
            Confirm the exact format, directions, and optional dose before
            submitting.
          </p>
        </div>
      </div>
      <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
        Medication option
        <select
          value={selectedProduct}
          onChange={(event) => onProduct(event.target.value)}
          className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink"
        >
          {options.map((option) => (
            <option key={option.product_id} value={option.product_id}>
              {option.purchased
                ? "Purchased · "
                : option.default_for_program
                  ? "Program default · "
                  : "Alternative · "}
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <div className="p-8 bg-sunken border border-line-subtle">
        <p className="type-label-xs text-ink-muted">EXACT CHART MEDICATION</p>
        <p className="type-body-sm font-medium mt-3 break-words">
          {selectedOption.medication || "Medication template unavailable"}
        </p>
      </div>
      <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
        Directions template
        <select
          value={customDirections ? "__custom" : selectedDirectionTemplateId}
          onChange={(event) => onDirectionTemplate(event.target.value)}
          className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink"
        >
          {selectedOption.direction_templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.is_default ? "Default · " : ""}
              {template.name} — {template.content}
            </option>
          ))}
          <option value="__custom">Custom directions…</option>
        </select>
      </label>
      {(customDirections || !selectedOption.direction_templates.length) && (
        <textarea
          value={directions}
          onChange={(event) => onDirections(event.target.value)}
          rows={4}
          aria-label="Medication directions"
          className="border border-line bg-raised px-10 py-8 type-body-sm outline-none focus:shadow-focus resize-y"
        />
      )}
      <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
        Dosage (optional)
        <input
          value={dosage}
          onChange={(event) => onDosage(event.target.value)}
          placeholder="Enter prescribed dose"
          className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink"
        />
      </label>
    </div>
  );
}

function PatientContext({
  item,
  data,
}: {
  item: PrescriptionWorkItem;
  data: PatientBriefResponse;
}) {
  const profile = data.profile;
  const { prescriptionMedication, otherActiveMedications: otherMedications } =
    prescriptionMedicationContext(data);
  return (
    <aside className="hidden lg:flex min-w-0 flex-col bg-sunken border-l border-line-subtle max-h-[calc(100vh-2rem)] overflow-y-auto">
      <div className="p-12 border-b border-line-subtle flex items-start gap-8">
        <UserRound size={17} className="mt-1 shrink-0" />
        <div className="min-w-0">
          <p className="type-label-xs text-ink-muted">PATIENT CONTEXT</p>
          <p className="type-heading-sm mt-3 truncate">{displayName(item)}</p>
          <p className="text-11 text-ink-muted mt-2 break-all">
            {profile.email}
          </p>
        </div>
      </div>
      <div className="p-12 flex flex-col gap-13">
        <div className="grid grid-cols-2 gap-x-10 gap-y-7">
          <Fact label="Age" value={profile.age?.toString() ?? "—"} />
          <Fact label="DOB" value={profile.date_of_birth ?? "—"} />
          <Fact label="Sex" value={profile.sex ?? "—"} />
          <Fact label="State" value={profile.state ?? "—"} />
        </div>

        <MasterProductEvidence
          patientId={item.patient.id}
          productId={item.product_id}
          orderId={item.id}
          legacyFallback={
            data.intake_evidence.length > 0 && (
              <section>
                <p className="type-label-xs text-ink-muted mb-6">
                  CLINICAL HIGHLIGHTS
                </p>
                <IntakeHighlights entries={data.intake_evidence} columns={1} />
              </section>
            )
          }
        />

        <section>
          <p className="type-label-xs text-ink-muted mb-6">
            MEDICATION FOR THIS PRESCRIPTION
          </p>
          <div
            className={`border p-7 ${
              prescriptionMedication
                ? "border-success/30 bg-success-wash"
                : "border-line-subtle bg-raised"
            }`}
          >
            <p className="type-body-sm font-medium">
              {prescriptionMedication?.name ?? item.medication ?? item.product}
            </p>
            {prescriptionMedication ? (
              <p className="text-11 text-ink-muted mt-2">
                {[
                  prescriptionMedication.dose,
                  prescriptionMedication.frequency,
                  prescriptionMedication.route,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : (
              <p className="text-11 text-ink-muted mt-2">
                No exact active chart-medication match is on file yet.
              </p>
            )}
          </div>
        </section>

        {otherMedications.length > 0 && (
          <details>
            <summary className="cursor-pointer type-label-xs text-ink-muted">
              OTHER ACTIVE CHART MEDICATIONS · {otherMedications.length}
            </summary>
            <div className="border border-line-subtle bg-raised divide-y divide-line-subtle mt-6">
              {otherMedications.slice(0, 6).map((medication) => (
                <div key={medication.id} className="p-7">
                  <p className="type-body-sm font-medium">{medication.name}</p>
                  <p className="text-11 text-ink-muted mt-2">
                    {[medication.dose, medication.frequency, medication.route]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
              ))}
            </div>
          </details>
        )}

        {item.support_escalations.length > 0 && (
          <section className="border border-warning/30 bg-warning-wash p-8">
            <div className="flex items-center gap-6 text-warning">
              <AlertTriangle size={14} />
              <p className="type-label-xs">SUPPORT ESCALATION</p>
            </div>
            <p className="type-body-sm font-medium mt-4">
              {item.support_escalations[0].title}
            </p>
            {item.support_escalations[0].note && (
              <p className="type-body-sm mt-3 whitespace-pre-wrap">
                {item.support_escalations[0].note}
              </p>
            )}
          </section>
        )}

        <div className="border border-success/30 bg-success-wash p-8 flex gap-6">
          <CheckCircle2 size={14} className="text-success shrink-0" />
          <p className="text-11 text-ink-muted">
            Product-linked intake and medication eligibility are rechecked by
            the server on submit.
          </p>
        </div>
      </div>
    </aside>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="type-label-xs text-ink-muted">{label}</p>
      <p className="type-body-sm mt-2 break-words">{value}</p>
    </div>
  );
}
import { PrescriptionSupply } from "./PrescriptionSupply";
